import {selectGlobalPresentId} from 'stores/selectors/undoRedoSelectors';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {Feedback, ViewMode, CommonButtonLabel, ServiceResponseCode} from '../../constants/dataEnum';
import {hideLoading, showLoading, showSnackbar} from '../actions/appStateAction';
import {resetCardState, resetGroupState} from '../actions/cardStateAction';
import {resetLabwareState} from '../actions/labwareStateAction';
import {hideModal, showModal} from '../actions/modalStateAction';
import {
  clearTemporaryUnsavedProtocolData,
  setLastOpenedProtocol,
  setOpenedProtocolCheckpoint,
} from '../actions/protocolAction';
import {resetProtocolEditorState, setPendingSaveExistStatus} from '../actions/protocolEditorAction';
import {resetTemporaryState} from '../actions/temporaryStateAction';
import {resetUndoRedoState} from '../actions/undoRedoAction';
import {resolveIntl} from './commonBusiness';
// eslint-disable-next-line import/no-cycle
import {showFormattedModal} from './commonModalBusiness';
import {saveState} from './stateBusiness';
import {globalAddUndoItem, globalUndoRedoClearItem, globalUndoRedoUpdatePresentState} from './undoRedoBusinessAction';

const intl = resolveIntl();

export const setProtocolCheckpoint =
  (protocol, saveProtocol = true) =>
  async (dispatch, getState) => {
    const {labwareState, cardState, protocolState} = getState();

    const openedProtocol = protocol || protocolState.lastOpenedProtocol || null;
    const historyId = selectGlobalPresentId(getState());

    const protocolData = {
      labwareState,
      cardState,
      openedProtocol,
      historyId,
    };

    await dispatch(setOpenedProtocolCheckpoint(protocolData));
    if (saveProtocol) {
      await dispatch(saveState(false));
      dispatch(setPendingSaveExistStatus(false));
    }
  };

export const showAutosaveConfirmationModal =
  (
    protocol,
    showDiscardButton,
    saveProtocol = () => {}, // saveProtocol func is passed through props to prevent dependency cycle
    saveCallback = () => {},
    discardCallback = () => {},
  ) =>
  async (dispatch, getState) => {
    const {protocolState, appState} = getState();
    const {lastOpenedProtocol} = protocolState;

    if (!lastOpenedProtocol) discardCallback();

    const {name: protocolName} = lastOpenedProtocol;

    const saveButton = {
      className: 'btn-lg',
      style: 'primary',
      handlerClick: async () => {
        const savedProtocol = await dispatch(saveProtocol(true));
        await dispatch(setProtocolCheckpoint(protocol));
        await dispatch(hideModal());
        dispatch(setPendingSaveExistStatus(false));
        dispatch(clearTemporaryUnsavedProtocolData());
        dispatch(
          showSnackbar(
            Feedback.SUCCESS,
            intl.formatMessage({
              id: 'protocolEditor.autosaveConfirmation.successSnackbar',
            }),
          ),
        );
        if (appState.viewMode === ViewMode.PROTOCOL_EDITOR) {
          dispatch(globalUndoRedoUpdatePresentState('protocolEditorState', {isPendingSaveExist: false}));
        }
        await dispatch(saveState(false));
        saveCallback(savedProtocol);
      },
      text: intl.formatMessage({
        id: 'modal.protocolEditor.autosaveConfirmation.firstButton',
      }),
    };

    const cancelButton = {
      className: 'btn-lg',
      style: showDiscardButton ? 'tertiary' : 'secondary',
      type: showDiscardButton && 'basic',
      handlerClick: async () => dispatch(hideModal()),
      text: intl.formatMessage({
        id: 'modal.protocolEditor.autosaveConfirmation.secondButton',
      }),
    };

    const discardButton = {
      className: 'btn-lg',
      style: 'secondary',
      type: 'danger',
      handlerClick: async () => {
        await dispatch(hideModal());

        dispatch(showLoading());
        await dispatch(setProtocolCheckpoint(protocol));
        dispatch(clearTemporaryUnsavedProtocolData());
        dispatch(hideLoading());

        dispatch(
          showSnackbar(
            Feedback.WARNING,
            intl.formatMessage({
              id: 'explorer.autosave.snackbar.changesDiscarded',
            }),
          ),
        );

        await dispatch(saveState(false));
        discardCallback();
      },
      text: intl.formatMessage({
        id: 'modal.protocolEditor.autosaveConfirmation.thirdButton',
      }),
    };

    const buttonOptions = [saveButton, cancelButton];

    if (showDiscardButton) buttonOptions.push(discardButton);

    dispatch(
      showModal(
        intl.formatMessage({id: 'modal.protocolEditor.autosaveConfirmation.title'}),
        intl.formatMessage({id: 'modal.protocolEditor.autosaveConfirmation.text'}, {protocolName}),
        buttonOptions,
        () => dispatch(hideModal()),
      ),
    );
  };

export const showNewUnsavedProtocolModal = () => async (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({id: 'modal.autosave.newUnsavedProtocol.title'}),
      <FormattedHTMLMessage id="modal.autosave.newUnsavedProtocol.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: async () => dispatch(hideModal()),
          text: intl.formatMessage({
            id: 'modal.autosave.newUnsavedProtocol.firstButton',
          }),
        },
      ],
      () => dispatch(hideModal()),
    ),
  );
};

export const resetProtocolEditorAndClearOpenedProtocol = () => (dispatch) => {
  dispatch(resetCardState());
  dispatch(resetGroupState());
  dispatch(resetLabwareState());
  dispatch(setLastOpenedProtocol(null));
  dispatch(resetUndoRedoState());
  dispatch(globalUndoRedoClearItem());
  dispatch(globalAddUndoItem());
  dispatch(resetTemporaryState());
  dispatch(resetProtocolEditorState());
};

export const showProtocolTessaraHasBeenDeleted =
  (callback, clearLastOpenedProtocol = true) =>
  (dispatch) => {
    dispatch(
      showFormattedModal('modal.tessara.tessaraProtocolDeleted', [
        {
          onClick: () => {
            if (clearLastOpenedProtocol) dispatch(setLastOpenedProtocol(null));
            callback();
          },
          label: CommonButtonLabel.GOT_IT,
        },
      ]),
    );
  };

export const getExportProtocolErrorMessage = (errorCode, payload) => {
  switch (errorCode) {
    case ServiceResponseCode.UNAUTH_FOLDER_ACTION_BECAUSE_NOT_TESSARA: {
      return intl.formatMessage({id: 'explorer.download.error.tessaraInsideFolder'});
    }
    case ServiceResponseCode.LABWARE_NOT_FOUND: {
      return (
        <FormattedHTMLMessage id="explorer.download.error.labwareNotFound" values={{labwareName: payload.toString()}} />
      );
    }
    default: {
      return intl.formatMessage({id: 'explorer.download.error'});
    }
  }
};
